import { useEffect, useRef, useState } from 'react';

export default function OurStory() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="our-story"
      ref={sectionRef}
      className="relative w-full bg-black overflow-hidden py-16 sm:py-24 md:py-32"
    >
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 60px, rgba(0,212,255,0.3) 60px, rgba(0,212,255,0.3) 61px), repeating-linear-gradient(90deg, transparent, transparent 60px, rgba(0,212,255,0.3) 60px, rgba(0,212,255,0.3) 61px)' }} />
      </div>
      <div className="absolute top-1/4 left-0 w-96 h-96 rounded-full bg-[#00d4ff]/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 rounded-full bg-[#00d4ff]/5 blur-3xl pointer-events-none" />

      <div className="relative z-10 w-[90%] max-w-[1400px] mx-auto">
        {/* Section label */}
        <div className={`flex items-center gap-4 mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="w-10 h-px bg-[#00d4ff]" />
          <span className="text-[#00d4ff] text-xs tracking-[0.3em] uppercase font-medium">Our Story</span>
        </div>

        {/* Top: headline + mission */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20 mb-12 md:mb-24 items-end">
          <div className={`transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight">
              Built From
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d4ff] to-white">
                The Ground Up
              </span>
            </h2>
          </div>
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-gray-400 text-base sm:text-lg font-light leading-relaxed mb-6">
              Motion Boyz isn't just a brand — it's a way of life. We were built by those who refused to stand still, who found identity in movement, and who wore their ambition on their sleeve — literally.
            </p>
            <p className="text-gray-500 text-sm sm:text-base font-light leading-relaxed">
              Every stitch carries intention. Every drop tells a story. We don't follow trends — we set the pace.
            </p>
          </div>
        </div>

        {/* Mission Statement Banner */}
        <div className={`relative rounded-2xl overflow-hidden transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="w-full h-[300px] sm:h-[400px] md:h-[500px] bg-black flex items-center justify-end relative">
            <img
              src="https://static.readdy.ai/image/7d0ce02f18177c7f8cb3537c80e36c26/a7b076fd39d0f8bf06dea88e30f89943.png"
              alt="Motion Boyz Mission"
              className="h-full w-auto object-contain object-right"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
          </div>
          <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-10 md:px-16">
            <p className="text-[#00d4ff] text-xs sm:text-sm tracking-[0.3em] uppercase mb-3 sm:mb-4 font-medium">Mission Statement</p>
            <h3 className="text-white text-xl sm:text-2xl md:text-4xl font-bold leading-snug max-w-2xl mb-4 sm:mb-6">
              "Got To Have Motion To Be Motion Boyz"
            </h3>
            <p className="text-gray-300 text-sm sm:text-base font-light max-w-xl leading-relaxed">
              We exist to give voice to a generation that lives at full speed — creating pieces that are as relentless, bold, and unstoppable as the people who wear them.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
